import Entity, { EntityTypes } from 'models/entity';
import Group from 'models/group';
import { DirectoryRef, IdentityProviderType } from 'models/identityProvider';
import { TeamsChannel } from 'models/teamsChannel';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';

export const parsePositiveIntegerRouteParam = (segment: string | undefined): number | undefined => {
  if (segment === undefined || !/^[1-9][0-9]*$/.test(segment)) return undefined;

  const value = Number.parseInt(segment, 10);

  return value <= 2147483647 ? value : undefined;
};

export const getEntityUrl = (entity: Entity): string => {
  let url: string = '';

  switch (entity.typeOfEntity) {
    case EntityTypes.Requirement:
      url = `/theme/${entity.entityId}`;
      break;
    case EntityTypes.Process:
      url = `/process/${entity.entityId}`;
      break;
    case EntityTypes.Risk:
      url = `/risk/${entity.entityId}`;
      break;
    case EntityTypes.Control:
      url = `/control/${entity.entityId}`;
      break;
    case EntityTypes.Objective:
      url = `/objective/${entity.entityId}`;
      break;
    case EntityTypes.Task:
      url = `/tasks/${entity.entityId}`;
      break;
    case EntityTypes.Asset:
      url = `/asset/${entity.entityId}`;
      break;
    case EntityTypes.KPI:
      url = `/kpi/${entity.entityId}/data`;
      break;
    case EntityTypes.Link:
      url = entity.data ?? '';
      break;
    case EntityTypes.TaskType:
      url = `/admin/content/forms?edit=${entity.entityId}`;
      break;
    case EntityTypes.ProcessingActivity:
      url = `/processingactivity/${entity.entityId}`;
      break;
    case EntityTypes.InternalAudit:
      // the execute tab, not the audit's landing tab: it is the one audit route a team member who
      // is not a manager may open. Mirrors the audit mails, which link to the same place.
      url = `/audit/${entity.entityId}/execute`;
      break;
    case EntityTypes.Project:
      url = `/project/${entity.entityId}`;
      break;
    default:
      break;
  }

  return url;
};

/**
 * The sub-tabs of the audit reporting tab. They are pivot keys in the scene, and the hash of the
 * reporting URL: `/audit/12/reporting#findings` opens the findings sub-tab.
 */
export const auditReportingPivotKeys = ['overview', 'findings', 'report', 'improve'] as const;

export type AuditReportingPivotKey = (typeof auditReportingPivotKeys)[number];

export const isAuditReportingPivotKey = (value: string): value is AuditReportingPivotKey =>
  (auditReportingPivotKeys as readonly string[]).includes(value);

export const getAuditExecuteUrl = (auditId: number): string => `/audit/${auditId}/execute`;

// Without a sub-tab the scene keeps the one it was on; with one it switches to it on arrival.
export const getAuditReportingUrl = (auditId: number, pivot?: AuditReportingPivotKey): string =>
  `/audit/${auditId}/reporting${pivot ? `#${pivot}` : ''}`;

export const navigateToExternalEntity = (entity: Entity, directory: DirectoryRef | undefined, orgUnitId: string) => {
  const url = getEntityUrl(entity);
  navigateToExternalUrl(url, directory, orgUnitId);
};

//The query that makes a link open in the right organization from outside the app: the directory to sign
//in with and the org unit. A Microsoft Entra directory travels as tid, any other provider as idp, because
//the client has no directory id it could sign in with and picks the provider instead. Mirrors
//DeepLinkHelper.TenantQuery in the API, which builds the links in mails and reports.
export function getDeepLinkQuery(directory: DirectoryRef | undefined, orgUnitId: string): string {
  if (!directory) return '';

  const params: string[] = [];
  const isEntra = directory.identityProviderType === IdentityProviderType.Entra;
  if (isEntra && directory.providerTenantId) {
    params.push(`tid=${directory.providerTenantId}`);
  }
  if (orgUnitId) {
    params.push(`ouid=${orgUnitId}`);
  }
  if (!isEntra) {
    params.push(`idp=${directory.identityProviderType}`);
  }

  return params.join('&');
}

//Builds an absolute link into the app for the given organization. Pass no directory for a url that is
//not an ISOPlanner page (a SharePoint site, a Teams channel): it is then returned as it is.
export function getExternalUrl(url: string, directory: DirectoryRef | undefined, orgUnitId: string): string {
  try {
    //Remove hash and append after tid/ouid
    let hash: string = '';
    const hashIdx: number = url.indexOf('#');
    if (hashIdx >= 0) {
      hash = url.slice(hashIdx);
      url = url.slice(0, hashIdx);
    }

    if (directory) {
      const query = getDeepLinkQuery(directory, orgUnitId);
      if (query) {
        url = url + (url.includes('?') ? '&' : '?') + query;
      }

      //add base URL if needed for navigation from other apps (e.g. Teams)
      const baseUrl = Config.get('App.BaseURL');
      if (!url.startsWith(baseUrl)) {
        url = baseUrl + url;
      }
    }

    if (hash) {
      url += hash;
    }

    //decode first to make sure we don't encode twice
    const decodedUrl = decodeURIFromHtml(url);
    if (!decodedUrl) return '';
    url = encodeURIToHtml(decodedUrl);

    return url;
  } catch (err) {
    logger.debug('getExternalUrl', err);

    return url;
  }
}

export function decodeURIFromHtml(url: string | undefined): string {
  try {
    if (!url) return '';

    let decodedUrl = decodeURI(url);

    //ampersand is not decoded but SharePoint can report such an Url with & encoded as %26 when a file name contains &
    decodedUrl = decodedUrl.replaceAll('%26', '&');
    //also, '&amp;' is not decoded and must be replaced
    decodedUrl = decodedUrl.replace(/&amp;/g, '&');

    return decodedUrl;
  } catch (err) {
    logger.debug('decodeURIFromHtml', err);

    return url || '';
  }
}

export function encodeURIToHtml(url: string | undefined): string {
  try {
    if (!url) return '';

    let encodedUrl = encodeURI(url);
    //encodeURI encodes % as %25, which breaks links to sharepoint folders, so we revert this
    encodedUrl = encodedUrl.replaceAll('%25', '%');

    return encodedUrl;
  } catch (err) {
    logger.debug('encodeURIToHtml', err);

    return url || '';
  }
}

//Appends the query parameters to a url, each name and value URL-encoded, after the url's own query when it
//has one and before its hash. A parameter without a value is left out.
export function appendUrlQueryParams(url: string, params: Record<string, string | undefined>): string {
  const query = Object.entries(params)
    .filter((param): param is [string, string] => !!param[1])
    .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
    .join('&');
  if (!query) return url;

  const hashIdx = url.indexOf('#');
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : '';
  const base = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
  const separator = !base.includes('?') ? '?' : base.endsWith('?') || base.endsWith('&') ? '' : '&';

  return base + separator + query + hash;
}

export function navigateToExternalUrl(
  url: string,
  directory: DirectoryRef | undefined,
  orgUnitId: string,
  shouldOpenNewTab: boolean = true,
) {
  //when you want to navigate to an ISOPlanner url, pass the tenant (its directory) and the org unit id,
  //otherwise pass undefined and an empty string
  url = getExternalUrl(url, directory, orgUnitId);

  if (shouldOpenNewTab) {
    window.open(url, '_blank', 'noreferrer');
  } else {
    window.location.href = url;
  }
}

export function GetCurrentURL(onlyLastPart: boolean) {
  const path = window.location.pathname;

  if (onlyLastPart) {
    return path.substring(path.lastIndexOf('/'));
  } else {
    return path;
  }
}

export function getCurrentURLWithoutLastPart(): string {
  const url = GetCurrentURL(false);

  const parts = url.split('/');
  parts.pop();

  return parts.join('/');
}

export function getURLLastPart(url: string): string | undefined {
  const parts = url.split('/');

  return parts.pop();
}

export function getURLBase(url: string): string | undefined {
  //return the domain and protocol part of the URL
  try {
    const a = document.createElement('a');
    a.href = url;

    return a.protocol + '//' + a.host;
  } catch (err) {
    logger.debug('getURLBase', err);

    return undefined;
  }
}

export function getURLWithoutLastPart(url: string | undefined): string | undefined {
  if (!url) return undefined;

  const parts = url.split('/');
  parts.pop();

  return parts.join('/');
}

export function getFilenameFromURL(url: string): string | undefined {
  try {
    const file = url.split('#').shift()?.split('?').shift()?.split('/').pop();

    return file ? decodeURI(file) : '';
  } catch (err) {
    logger.debug('getFilenameFromURL', err);

    return '';
  }
}

export function getRelativeURL(url: string): string {
  const a = document.createElement('a');
  a.href = url;

  return a.pathname;
}

export function validateURL(str: string): boolean {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator

  return !!pattern.test(str);
}

//a complete https:// address with a host and without a query or fragment, as an API endpoint must be
//(validateURL also accepts bare domains and http)
export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());

    return url.protocol === 'https:' && url.host !== '' && url.search === '' && url.hash === '';
  } catch {
    return false;
  }
}

export function removeUrlHash() {
  window.history.replaceState(
    '',
    document.title,
    window.location.origin + window.location.pathname + window.location.search,
  );
}

//Clears the query string of the current URL. Scenes call this after reading parameters that are meant
//to act once, so a refresh does not apply them again. Pass keepParams for parameters that describe the
//view itself rather than a one-off action, so they survive that clean-up.
export function removeUrlQuery(keepParams?: string[]) {
  const search = keepParams ? getSearchWithParams(window.location.search, keepParams) : '';
  window.history.replaceState('', document.title, window.location.origin + window.location.pathname + search);
}

//Teams is a Microsoft 365 feature: the directory here is always the Entra tenant id
export function getTeamsChannelUrl(channel: TeamsChannel, group: Group, entraTenantId: string | undefined): string {
  let url = encodeURI(
    `https://teams.microsoft.com/l/channel/${channel.channelId}/${channel.name}?groupId=${group.id}&tenantId=${entraTenantId}`,
  );
  url = url.replaceAll('@', '%40'); //channels contain @ which must be encoded for teams

  return url;
}

//Reads a query parameter from a url the same way the app reads tid/ouid from its start url (see getTenantFromRoute),
export function getUrlQueryParam(url: string, name: string): string | undefined {
  const queryIdx = url.indexOf('?');
  if (queryIdx === -1) return undefined;

  let query = url.substring(queryIdx);
  const hashIdx = query.indexOf('#');
  if (hashIdx >= 0) query = query.substring(0, hashIdx);
  //urls extracted from html can still have the & encoded
  query = query.replace(/&amp;/gi, '&');

return new URLSearchParams(query).get(name)?.trim() || undefined;
}

//Resolves a link from a SharePoint page against that page, the way the browser does in SharePoint itself.
//Absolute links are kept, relative ones get the SharePoint host and path.
export function resolveSharePointLinkUrl(url: string | undefined, pageWebUrl: string | undefined): string {
  if (!url) return '';

  try {
    const resolved = new URL(url, pageWebUrl);
    if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return '';

    return resolved.href;
  } catch {
    return '';
  }
}

// Rebuilds a query string from the named parameters only. Used to carry a scene-level choice such as
// frameworkView across a pivot click, which otherwise navigates to the bare sub-tab path.
export function getSearchWithParams(search: string, names: string[]): string {
  const source = new URLSearchParams(search);
  const target = new URLSearchParams();

  names.forEach((name) => {
    const value = source.get(name);
    if (value) {
      target.append(name, value);
    }
  });

  const result = target.toString();

  return result ? `?${result}` : '';
}
